import { useEffect, useState } from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isEntitlementError } from "@/lib/api/billing";
import { showToast } from "@/hooks/useToast";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { TooltipProvider } from "@/components/ui/tooltip";
import { router } from "@/router";
import { useAuthStore } from "@/stores/auth-store";
import { LiveUpdatesProvider } from "@/providers/LiveUpdatesProvider";
// Previous word-cycling "Connected" splash — kept for easy reversion:
// import { SplashScreen } from "@/components/splash-screen";
import { UnboxingSplash } from "@/components/unboxing-splash";
import { BrandThemeProvider } from './hooks/brand-theme-provider';

/**
 * Boot-splash frequency. Flip this one constant to change the behavior:
 *   'always'           → play on every full page load / refresh
 *   'once-per-session' → play only the first load of a browser session (current:
 *                        a ~5s splash on every reload slowed everyday use)
 */
const SPLASH_MODE: 'always' | 'once-per-session' = 'once-per-session';
const SPLASH_SESSION_KEY = 'compbox-splash-shown';

const queryClient = new QueryClient({
  // Every mutation in the app passes through here (ENG-200).
  //
  // There are 70+ mutation call sites; disabling each of their buttons
  // individually would be a large change that still left any site it missed
  // failing silently. Catching the refusal centrally means a blocked write can
  // never be silent, whichever control triggered it, including ones added
  // later. The banner explains the state; this explains the individual attempt.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isEntitlementError(error)) {
        showToast(
          "Your organization is read-only — this change was not saved. Visit Billing to restore access.",
          6000
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

function App(): React.ReactNode {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const [showSplash, setShowSplash] = useState(() => {
    // 'once-per-session' skips the splash if it has already played this session.
    // 'always' plays on every full page load (a fresh App mount).
    if (SPLASH_MODE === 'once-per-session' && sessionStorage.getItem(SPLASH_SESSION_KEY)) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const handleSplashComplete = () => {
    if (SPLASH_MODE === 'once-per-session') {
      sessionStorage.setItem(SPLASH_SESSION_KEY, '1');
    }
    setShowSplash(false);
  };

  return (
    <BrandThemeProvider>
      <QueryClientProvider client={queryClient}>
        <LiveUpdatesProvider>
          <ThemeProvider defaultTheme="dark" storageKey="compbox-ui-theme">
            <TooltipProvider>
              {showSplash ? (
                <UnboxingSplash onComplete={handleSplashComplete} />
              ) : (
                <RouterProvider router={router} />
              )}
            </TooltipProvider>
          </ThemeProvider>
        </LiveUpdatesProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrandThemeProvider>
  );
}

export default App;
